import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, BadgeCheck, Plus, ChevronDown } from "lucide-react";
import { agencyApi, apiErr } from "./agencyApi";
import { AgencyAvatar } from "./AgencyAvatar";
import { WorkViewer, WorkThumb, hasPlayableSamples } from "./WorkViewer";
import { splitDashSegments } from "./textUtils";

// The discovery API returns `skills` as a free-text string (and sometimes null), not an array.
// Normalise defensively so the whole screen can't crash on a single record.
function skillList(skills) {
  if (Array.isArray(skills)) return skills.filter(Boolean);
  if (typeof skills === "string" && skills.trim()) return skills.split(/[,;·|]/).map((s) => s.trim()).filter(Boolean);
  return [];
}

export default function AgencyDiscover() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const reqId = sp.get("req");
  const projectId = sp.get("project");

  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [city, setCity] = useState("");
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [toast, setToast] = useState("");
  const [viewing, setViewing] = useState(null);

  const run = useCallback(() => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (role.trim()) params.role = role.trim();
    if (city.trim()) params.city = city.trim();
    agencyApi.discovery(params).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [q, role, city]);

  useEffect(() => { run(); }, []); // eslint-disable-line

  const addToPreferred = async (fid) => {
    if (!reqId) return;
    try { await agencyApi.addCandidate(reqId, fid); setToast("Added to your preferred crew."); }
    catch (e) { setToast(apiErr(e)); }
    setTimeout(() => setToast(""), 2600);
  };

  return (
    <div data-testid="agency-discover">
      <h1 className="ag-h1 ag-entrance" style={{ fontSize: 36, marginBottom: 8 }}>Who do you need for your production?</h1>
      <p className="ag-body ag-entrance" style={{ color: "var(--muted)", marginBottom: 8 }}>Search NJM's vetted crew. NJM manages every relationship: you shortlist, NJM confirms.</p>
      {reqId && (
        <div className="ag-alert ag-alert-ok ag-entrance" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }} data-testid="agency-discover-req-banner">
          Adding candidates to your preferred crew for this production.
          <button className="ag-link" onClick={() => nav(`/agency/projects/${projectId}`)}>Back to project</button>
        </div>
      )}

      <div className="ag-entrance-2" style={{ display: "flex", gap: 12, margin: "22px 0 34px", flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "1 1 320px" }}>
          <Search size={17} style={{ position: "absolute", left: 14, top: 15, color: "#8A8A8A" }} />
          <input className="ag-input" style={{ paddingLeft: 42 }} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="e.g. DOP in Mumbai for executive interviews" data-testid="agency-discover-search" />
        </div>
        <input className="ag-input" style={{ flex: "0 1 160px" }} value={role} onChange={(e) => setRole(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="Role" data-testid="agency-discover-role" />
        <input className="ag-input" style={{ flex: "0 1 160px" }} value={city} onChange={(e) => setCity(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="City" data-testid="agency-discover-city" />
        <button className="ag-btn ag-btn-dark" onClick={run} data-testid="agency-discover-submit">Search</button>
      </div>

      {toast && <div className="ag-alert ag-alert-ok" data-testid="agency-discover-toast">{toast}</div>}

      {!items ? <div className="ag-small">Loading…</div> : items.length === 0 ? (
        <div className="ag-empty">No crew matched. Try a broader search.</div>
      ) : (
        <div className="ag-grid ag-grid-3 ag-entrance-3">
          {items.map((m) => (
            <div key={m.id} className="ag-card ag-card-hover" data-testid="agency-discover-card">
              <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 14 }}>
                <AgencyAvatar freelancerId={m.id} hasPhoto={m.has_profile_photo} name={m.name} className="ag-avatar" />
                <div style={{ minWidth: 0 }}>
                  <div className="ag-h3" style={{ fontSize: 16, display: "flex", alignItems: "center", gap: 6 }}>
                    {m.name}{m.njm_verified && <BadgeCheck size={15} color="#8A6A00" />}
                  </div>
                  <div className="ag-small">{m.primary_role}{m.city ? ` · ${m.city}` : ""}</div>
                </div>
              </div>
              {m.njm_verified && <span className="ag-pill ag-pill-gold" style={{ marginBottom: 12 }}>NJM endorsed</span>}
              {skillList(m.skills).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "10px 0 14px" }}>
                  {skillList(m.skills).slice(0, 4).map((s, i) => <span key={i} className="ag-pill ag-pill-neutral">{s}</span>)}
                </div>
              )}
              {expanded === m.id && (
                <div style={{ borderTop: "1px solid var(--line)", paddingTop: 12, marginBottom: 12 }}>
                  {m.bio && (
                    <div style={{ marginBottom: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                      {splitDashSegments(m.bio).map((line, i) => <p key={i} className="ag-small" style={{ margin: 0 }}>{line}</p>)}
                    </div>
                  )}
                  {hasPlayableSamples(m.work_samples) ? (
                    <>
                      <div className="ag-label" style={{ marginBottom: 8 }}>Selected work <span className="ag-small" style={{ fontWeight: 400, color: "var(--faint)" }}>· tap to view in-portal</span></div>
                      <div style={{ display: "flex", gap: 8, overflowX: "auto" }}>
                        {m.work_samples.slice(0, 6).map((w, i) => (
                          <WorkThumb key={w.id || i} sample={w} label={m.primary_role || m.name} onOpen={setViewing} />
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="ag-small" style={{ color: "var(--faint)" }} data-testid="agency-discover-showreel-on-request">Full showreel available on request through your NJM producer.</p>
                  )}
                </div>
              )}
              <div style={{ display: "flex", gap: 10, alignItems: "center", borderTop: "1px solid var(--line)", paddingTop: 14 }}>
                <button className="ag-btn ag-btn-ghost" style={{ padding: "10px 14px" }} onClick={() => setExpanded(expanded === m.id ? null : m.id)} data-testid="agency-discover-quicklook">
                  <ChevronDown size={14} style={{ transform: expanded === m.id ? "rotate(180deg)" : "none", transition: "transform .2s ease" }} /> Quick look
                </button>
                {reqId && <button className="ag-btn ag-btn-gold" style={{ padding: "10px 14px" }} onClick={() => addToPreferred(m.id)} data-testid="agency-discover-add"><Plus size={14} /> Add to preferred</button>}
                <span className="ag-small" style={{ marginLeft: "auto", color: "var(--faint)" }} title="View this creative's work inside the portal. NJM manages all contact and booking; there are no external links or contact details by design.">Portal only</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {viewing && <WorkViewer sample={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
